import { ConversationAiUsage } from '../components/ConversationAiUsage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AgentId, ChatTurn, MessageCitation, MessageId, SessionId } from '@prakash-software/shared';
import { colorForVisitor, pseudonymForUuid } from '@prakash-software/shared';
import {
  newMessageId,
  useConversationsStore,
  type Conversation as ConversationT,
  type ConversationMessage,
  type NotesSaveState,
} from '../stores/conversations';
import { putNotes } from '../lib/notes-api';
import { useVisitorsStore } from '../stores/visitors';
import { useSiteNames, siteBadgeStyle } from '../stores/sites';
import { useAgentStore } from '../stores/agent';
import { useFeatureFlag } from '../stores/feature-flags';
import { createVisitorPlaceholder, type PlaceholderHandle } from '../lib/agent-placeholder';
import { startAgentOffer, watchForOffer, type AgentPeerLink } from '../lib/webrtc-link';
import { reportIceFailed } from '../lib/webrtc-metrics';
import { useOutgoingCallsStore } from '../stores/outgoing-calls';
import { useTypingStore } from '../stores/typing';
import { emitSystemMessage, hangupAndNotify } from '../lib/call-control';
import { markStart } from '../lib/call-timer';
import { playRingtone, stopRingtone } from '../lib/ringtone';
import { replaceVideoTrack } from '@prakash-software/shared/webrtc';
import { listCameras, setPreferredCameraId } from '../lib/media-devices';
import { VideoControl } from '../components/VideoControl';
import { RichText } from '../components/RichText';
import { startRecording, type RecordingHandle } from '../lib/recording/recorder';
import {
  setSessionStarred,
  deleteSession,
  endConversation,
  sendConversationEmail,
  triggerConversationWebhook,
  listConversationAssignees,
  updateConversationInbox,
  type ConversationAssignee,
} from '../lib/sessions-api';
import { isApiError } from '../lib/api';
import { retryMessageDelivery } from '../lib/delivery-api';
import { createTicket, listTickets } from '../lib/tickets-api';
import { VisitorProfile } from '../components/VisitorProfile';
import { ShopPanel } from '../components/ShopPanel';
import { useShopifyConnected } from '../lib/shopify-api';
import { IntentField } from '../components/IntentField';
import { RecordingsPanel } from '../components/RecordingsPanel';
import { useRecordingsStore } from '../stores/recordings';
import { DeviceContext } from '../components/DeviceContext';
import { PageTimeline } from '../components/PageTimeline';
import { generateSummaryForConversation } from '../lib/ai-orchestrator';
import { hydrateConversationDetail } from '../lib/hydration';
import { useNotificationsStore } from '../stores/notifications';
import { getBus } from '@prakash-software/shared';
import { isVisitorLive } from '../lib/visitor-live';
import { resolveAvatarUrl } from '../lib/server-origin';
import {
  Archive,
  Bot,
  Check,
  ChevronDown,
  Star,
  Trash2,
  Mail,
  FileText,
  Maximize2,
  Minimize2,
  MonitorUp,
  Paperclip,
  PhoneOff,
  PictureInPicture2,
  Send,
  Slash,
  Smile,
  Sparkles,
  UserCheck,
  Video as VideoIcon,
  Ticket as TicketIcon,
  X as XIcon,
} from 'lucide-react';
import { EmojiPicker } from '../components/EmojiPicker';
import { assistantErrorMessage, suggestReply } from '../lib/assistant-api';
import { loadCommandsForSite, type SiteCommand } from '../lib/commands-api';
import { fetchHistoryFilters } from '../lib/history-api';
import { describeActions, expandPlaceholders, hasActions, mergeTags, type MacroActions } from '../lib/macro';
import type { ChatAttachment } from '@prakash-software/shared';
import { uploadChatAttachment } from '../lib/attachments-api';
import { getConversationSurvey, type ConversationSurvey } from '../lib/csat-api';
import styles from './Conversation.module.css';

import { formatDateTime, formatTime, formatRelativeOrAbsolute } from '../lib/datetime';
import { confirmDialog } from '../stores/confirm';

// Roles shown in the agent's video stage. The remote (visitor) feed is the
// main view by default; the agent's self-view floats as a PIP. A tile can be
// pinned to the main view via full-screen-any-tile (§1.4). PIPs stack
// left-to-right with the self-view (agent) leftmost (§1.2).
type StageRole = 'agent' | 'visitor';
const PIP_ORDER: ReadonlyArray<StageRole> = ['agent', 'visitor'];

// Right-panel tabs. Chat is only offered during a call (otherwise the chat is
// the main area). Order matches the mocks: [Chat] · Context · Visitor · Rec · Notes.
// Shop sits after Visitor, and only on sites with a Shopify store connected.
type SideTab = 'chat' | 'context' | 'visitor' | 'shop' | 'recordings' | 'notes';
const TAB_LABEL: Record<SideTab, string> = {
  chat: 'Chat',
  context: 'Context',
  visitor: 'Visitor',
  shop: 'Shop',
  recordings: 'Rec',
  notes: 'Notes',
};
const IN_CALL_TABS: ReadonlyArray<SideTab> = ['chat', 'context', 'visitor', 'shop', 'recordings', 'notes'];
const NO_CALL_TABS: ReadonlyArray<SideTab> = ['context', 'visitor', 'shop', 'recordings', 'notes'];

export function Conversation() {
  const { id } = useParams<{ id: string }>();
  const sessionId = id as SessionId | undefined;
  const conversation = useConversationsStore((s) =>
    sessionId ? s.byId.get(sessionId) ?? null : null,
  );
  const navigate = useNavigate();

  // Keep the active id in sync with the URL so the right rail highlights properly.
  const setActive = useConversationsStore((s) => s.setActive);
  useEffect(() => {
    setActive(sessionId ?? null);
    return () => setActive(null);
  }, [sessionId, setActive]);

  // Background hard-reload of the full conversation detail from the server on
  // open AND on every conversation switch. The cached row (if any) is already
  // on screen via the selector above — this fetch runs underneath and
  // reconciles server truth into the store when it lands, so the agent never
  // has to manually refresh to see the latest messages / owner / star.
  // Guarded by `ignore`: if the agent navigates to a different conversation
  // before the response arrives, we drop it instead of letting a stale fetch
  // write back. The `[sessionId]` dep makes this fire once per open/switch,
  // not on every render or store update.
  useEffect(() => {
    if (!sessionId) return;
    let ignore = false;
    void hydrateConversationDetail(sessionId, () => ignore);
    return () => {
      ignore = true;
    };
  }, [sessionId]);

  // Clear the unread counter on the matching visitor row.
  useEffect(() => {
    if (!sessionId) return;
    const conv = useConversationsStore.getState().byId.get(sessionId);
    if (conv) useVisitorsStore.getState().markRead(conv.visitorId);
  }, [sessionId]);

  if (!conversation || !sessionId) {
    return <Missing sessionId={sessionId} onBack={() => navigate('/dashboard')} />;
  }
  return <ActiveConversation conversation={conversation} />;
}

function Missing({ sessionId, onBack }: { sessionId?: string; onBack: () => void }) {
  return (
    <section className={styles.root}>
      <p className={styles.kicker}>Conversation</p>
      <h1 className={styles.title}>That session is gone.</h1>
      <p className={styles.empty}>
        Session <code>{sessionId ?? '—'}</code> was closed or never existed. Open a new one from
        the monitor.
      </p>
      <button type="button" className={styles.linkBtn} onClick={onBack}>
        ← Back to monitor
      </button>
    </section>
  );
}

function ActiveConversation({ conversation }: { conversation: ConversationT }) {
  // Site this conversation is on — labeled in the header so an agent with access
  // to multiple sites always knows which one they're replying on.
  const { byId: siteById } = useSiteNames();
  const append = useConversationsStore((s) => s.append);
  const setStarred = useConversationsStore((s) => s.setStarred);
  const setSummary = useConversationsStore((s) => s.setSummary);
  const setInbox = useConversationsStore((s) => s.setInbox);
  const close = useConversationsStore((s) => s.close);
  const me = useAgentStore((s) => s.current);
  const navigate = useNavigate();
  const [summarising, setSummarising] = useState(false);
  const [showSummaryPicker, setShowSummaryPicker] = useState(false);
  const [assignees, setAssignees] = useState<ConversationAssignee[]>([]);
  // The site's departments, for moving the conversation between them.
  const [siteDepartments, setSiteDepartments] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    if (!conversation.siteId) return;
    let cancelled = false;
    void fetchHistoryFilters()
      .then((f) => {
        if (!cancelled) setSiteDepartments(f.departments.filter((d) => d.siteId === conversation.siteId));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [conversation.siteId]);
  const [inboxBusy, setInboxBusy] = useState(false);
  const [tagDraft, setTagDraft] = useState('');

  useEffect(() => {
    let cancelled = false;
    void listConversationAssignees(conversation.sessionId)
      .then((rows) => { if (!cancelled) setAssignees(rows); })
      .catch(() => { if (!cancelled) setAssignees([]); });
    return () => { cancelled = true; };
  }, [conversation.sessionId]);

  async function updateInbox(
    patch: Parameters<typeof updateConversationInbox>[1],
  ) {
    if (inboxBusy) return;
    setInboxBusy(true);
    try {
      const result = await updateConversationInbox(conversation.sessionId, patch);
      setInbox(conversation.sessionId, {
        status: result.status,
        priority: result.priority,
        tags: result.tags,
        ...(result.department !== undefined
          ? { departmentId: result.department?.id ?? null, departmentName: result.department?.name ?? null }
          : {}),
        ...(result.assignee
          ? {
              ownerAgentId: result.assignee.id,
              ownerAgentName: result.assignee.name,
              ownerAgentAvatarUrl: result.assignee.avatarUrl ?? undefined,
            }
          : result.assignee === null
            ? { ownerAgentId: undefined, ownerAgentName: undefined, ownerAgentAvatarUrl: undefined }
            : {}),
      });
      if (Object.prototype.hasOwnProperty.call(patch, 'assigneeId')) {
        useConversationsStore.getState().setOwner(
          conversation.sessionId,
          'agent',
          result.assigneeId ?? undefined,
        );
      }
    } catch {
      useNotificationsStore.getState().push({
        kind: 'system',
        title: 'Couldn’t update inbox fields',
        body: 'The server rejected the change. Please try again.',
        ttl: 5000,
      });
    } finally {
      setInboxBusy(false);
    }
  }

  function addTag() {
    const tag = tagDraft.trim().toLowerCase().slice(0, 32);
    if (!tag) return;
    const next = [...new Set([...(conversation.tags ?? []), tag])].slice(0, 10);
    setTagDraft('');
    void updateInbox({ tags: next });
  }

  const [draft, setDraft] = useState('');
  // "Suggest reply": the site's AI drafts the agent's next message into the
  // composer for them to edit and send.
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const visitorTyping = useTypingStore((s) => s.typing.has(conversation.sessionId));
  const hasRecording = useRecordingsStore((s) => s.sessionIds.has(conversation.sessionId));
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [commands, setCommands] = useState<SiteCommand[]>([]);
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const [slashIdx, setSlashIdx] = useState(0);
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Inbox actions of the canned reply that produced the current draft. They
  // apply when that draft is sent and are dropped if the agent clears it, so
  // picking a macro by mistake changes nothing.
  const pendingMacro = useRef<MacroActions | null>(null);
  useEffect(() => {
    if (!draft.trim()) pendingMacro.current = null;
  }, [draft]);

  // Load /commands once we know which site the conversation lives on. The
  // cache in commands-api handles dedup so repeated mounts are cheap.
  useEffect(() => {
    if (!conversation.siteId) return;
    let cancelled = false;
    void loadCommandsForSite(conversation.siteId).then((cmds) => {
      if (!cancelled) setCommands(cmds);
    });
    return () => {
      cancelled = true;
    };
  }, [conversation.siteId]);

  // Slash autocomplete — derive matches from the current draft. A leading
  // "/" followed by letters/digits opens the menu; anything else closes it.
  const slashMatches: SiteCommand[] = (() => {
    if (slashQuery === null) return [];
    const q = slashQuery.toLowerCase();
    return commands.filter((c) => c.shortcut.toLowerCase().startsWith(q)).slice(0, 8);
  })();

  // Compute slash state from the draft string (`/foo` only at the start).
  useEffect(() => {
    const slashMatch = draft.match(/^\/([a-zA-Z0-9_-]*)$/);
    if (slashMatch) {
      setSlashQuery(slashMatch[1] ?? '');
      setSlashIdx(0);
    } else {
      setSlashQuery(null);
    }
  }, [draft]);

  // When the slash menu opens, pull the latest commands from the DB so an
  // admin's just-added command is searchable at runtime — no page reload. The
  // fetch is deduped/cached in commands-api, so reopening the menu is cheap.
  const slashOpen = slashQuery !== null;
  useEffect(() => {
    if (!slashOpen || !conversation.siteId) return;
    let cancelled = false;
    void loadCommandsForSite(conversation.siteId, { force: true }).then((cmds) => {
      if (!cancelled) setCommands(cmds);
    });
    return () => {
      cancelled = true;
    };
  }, [slashOpen, conversation.siteId]);

  function insertAtCursor(text: string) {
    const node = composerInputRef.current;
    if (!node) {
      setDraft((d) => d + text);
      return;
    }
    const start = node.selectionStart ?? draft.length;
    const end = node.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + text + draft.slice(end);
    setDraft(next);
    requestAnimationFrame(() => {
      const n = composerInputRef.current;
      if (!n) return;
      const caret = start + text.length;
      n.focus();
      n.setSelectionRange(caret, caret);
    });
  }

  function applySlash(cmd: SiteCommand) {
    // Placeholders are filled here, not on send, so the agent reviews the
    // exact text the visitor will get.
    const site = conversation.siteId ? siteById.get(conversation.siteId) : undefined;
    const body = expandPlaceholders(cmd.body, {
      visitorName: visitorRow?.identity.name ?? null,
      visitorEmail: visitorRow?.identity.email ?? null,
      agentName: me?.name ?? null,
      siteName: site?.name ?? null,
    });
    setDraft(body);
    pendingMacro.current = hasActions(cmd.actions) ? cmd.actions : null;
    setSlashQuery(null);
    requestAnimationFrame(() => {
      const n = composerInputRef.current;
      if (!n) return;
      n.focus();
      n.setSelectionRange(body.length, body.length);
    });
  }

  // Fast client feedback; the server repeats this validation and verifies the
  // bytes actually stored in S3 before returning an attachment token.
  const MAX_ATTACH_BYTES = 5 * 1024 * 1024;
  async function pickAttachments(files: FileList | null) {
    if (!files || files.length === 0) return;
    if (!conversation.siteId) {
      useNotificationsStore.getState().push({
        kind: 'system',
        title: 'Upload unavailable',
        body: 'This conversation is missing its site assignment.',
        ttl: 6000,
      });
      return;
    }
    const accepted: ChatAttachment[] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_ATTACH_BYTES) {
        useNotificationsStore.getState().push({
          kind: 'system',
          title: 'File too large',
          body: `"${f.name}" is over the 5 MB limit.`,
          ttl: 6000,
        });
        continue;
      }
      try {
        accepted.push(await uploadChatAttachment(f, conversation.sessionId, conversation.siteId));
      } catch {
        useNotificationsStore.getState().push({
          kind: 'system',
          title: 'Upload rejected',
          body: `“${f.name}” failed type validation, storage, or malware scanning.`,
          ttl: 6000,
        });
      }
    }
    if (accepted.length > 0) setAttachments((cur) => [...cur, ...accepted]);
  }

  // Visitor presence — used to disable composing when the visitor has left
  // and to drive the LIVE indicator. Reads the row reactively so this updates
  // the moment the bus delivers a `visitor:presence` event.
  const visitorRow = useVisitorsStore((s) => s.rows.get(conversation.visitorId));
  // Tick every 5s so the LIVE indicator decays without a fresh presence event.
  const [liveTick, setLiveTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setLiveTick((t) => t + 1), 5_000);
    return () => window.clearInterval(id);
  }, []);
  void liveTick;
  const visitorIsLive = visitorRow ? isVisitorLive(visitorRow) : false;
  // A customer on an asynchronous channel (the API channel, WhatsApp, email) is never
  // on a socket but is always reachable: replies queue and are delivered by the
  // channel. Only web visitors can be "offline".
  // CSAT outcome (a survey exists only once the conversation has ended).
  const [survey, setSurvey] = useState<ConversationSurvey | null>(null);
  useEffect(() => {
    let ignore = false;
    setSurvey(null);
    getConversationSurvey(conversation.sessionId)
      .then((sv) => {
        if (!ignore) setSurvey(sv);
      })
      .catch(() => {
        // Optional extra: the conversation works without it.
      });
    return () => {
      ignore = true;
    };
  }, [conversation.sessionId]);

  const offChannel = !!conversation.channel && conversation.channel !== 'web';
  const channelLabel =
    conversation.channel === 'whatsapp' ? 'WhatsApp'
      : conversation.channel === 'instagram' ? 'Instagram'
      : conversation.channel === 'messenger' ? 'Messenger'
      : conversation.channel === 'email' ? 'Email'
      : 'API';
  const visitorOffline = offChannel ? false : !visitorIsLive;
  const site = conversation.siteId ? siteById.get(conversation.siteId) : undefined;
  const siteName = site?.name ?? null;

  // ─── Video tile: canvas placeholder until the visitor's stream lands ─
  // Canvas runs until `ontrack` fires on the peer connection; at that point
  // we swap the <video>'s srcObject to the real remote stream and stop the
  // placeholder. Role is determined by who initiated:
  //   - We initiated (callerSessions has us): act as offerer (startAgentOffer)
  //   - They initiated: act as answerer (watchForOffer)
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const placeholderRef = useRef<PlaceholderHandle | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<RecordingHandle | null>(null);
  // Last-known visitor screen-share state, so a screen:share event that
  // arrives before the recorder exists is applied once it starts.
  const remoteSharingRef = useRef(false);
  // Reactive mirror of remoteSharingRef, for the screenshare status capsule.
  const [remoteSharing, setRemoteSharing] = useState(false);
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [recordingSeq, setRecordingSeq] = useState(0);
  // Camera source selection — list of available cameras, the active one, and
  // whether the picker menu is open.
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraId, setCurrentCameraId] = useState<string | null>(null);
  const [showCamMenu, setShowCamMenu] = useState(false);
  // Full-screen-any-tile (§1.4): which tile is pinned to the main view, if any.
  // null = priority layout (remote/visitor in main, self/agent as PIP).
  const [pinnedRole, setPinnedRole] = useState<StageRole | null>(null);
  const mainRole: StageRole = pinnedRole ?? 'visitor';
  const slotFor = (role: StageRole) => (role === mainRole ? 'main' : 'pip');
  const pipIndexFor = (role: StageRole) =>
    PIP_ORDER.filter((r) => r !== mainRole).indexOf(role);
  const toggleFullscreenTile = (role: StageRole) =>
    setPinnedRole((p) => (p === role ? null : role));
  // Right-panel tab shown during a call (§4.1). The same panels live in the
  // non-call accordion; here they become tabs. Default to Chat.
  const [sideTab, setSideTab] = useState<SideTab>('chat');
  const shopConnected = useShopifyConnected(conversation.siteId);
  // Outside a call there's no Chat tab (chat is the main area), so a lingering
  // 'chat' selection falls back to Context; so does Shop on a site without a store.
  const effectiveTab: SideTab =
    sideTab === 'shop' && !shopConnected
      ? 'context'
      : conversation.inVideo ? sideTab : sideTab === 'chat' ? 'context' : sideTab;
  const isCaller = useOutgoingCallsStore((s) =>
    s.callerSessions.has(conversation.sessionId),
  );
  const clearCaller = useOutgoingCallsStore((s) => s.clearCaller);
  useEffect(() => {
    if (!conversation.inVideo || !me) return;
    const handle = createVisitorPlaceholder({
      mode: 'waiting',
      visitorName: conversation.visitorName,
    });
    placeholderRef.current = handle;
    if (videoRef.current) videoRef.current.srcObject = handle.stream;

    const handlers = {
      sessionId: conversation.sessionId,
      agentId: me.agentId,
      visitorId: conversation.visitorId,
      onRemoteStream: (stream: MediaStream) => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = false;
        }
        if (placeholderRef.current) {
          placeholderRef.current.stop();
          placeholderRef.current = null;
        }
        remoteStreamRef.current = stream;
        maybeStartRecorder();
      },
      onConnected: () => {
        handle.setMode('live');
        // Stamp the call start so the end-call system chip can report
        // duration. `markStart` is idempotent so re-firing is safe.
        markStart(conversation.sessionId);
      },
      onIceFailed: () =>
        reportIceFailed({ sessionId: conversation.sessionId, role: 'agent' as const }),
      // Visitor's peer vanished (reload/crash/drop) — end the call so the
      // agent isn't left on a frozen remote tile. Mirrors a remote hangup.
      onPeerLost: () => hangupAndNotify(conversation.sessionId, 'ended'),
      onReady: ({ pc, localStream }: { pc: RTCPeerConnection; localStream: MediaStream }) => {
        pcRef.current = pc;
        localStreamRef.current = localStream;
        // Attach the agent's own camera to the local-self tile.
        if (localVideoRef.current) localVideoRef.current.srcObject = localStream;
        maybeStartRecorder();
      },
    };

    // Start recording once both local and remote streams are wired. Either
    // side may arrive first depending on caller/answerer role.
    function maybeStartRecorder() {
      if (recorderRef.current) return;
      const remote = remoteStreamRef.current;
      const local = localStreamRef.current;
      if (!remote || !local) return;
      const rec = startRecording({
        sessionId: conversation.sessionId,
        remote,
        local,
        labels: {
          agent: me?.name ?? 'Agent',
          visitor: conversation.visitorName || 'Visitor',
        },
        onChunk: (seq) => setRecordingSeq(seq + 1),
        onError: (err) => {
           
          console.warn('[recording] MediaRecorder error', err);
        },
      });
      // Re-apply any share state that landed before the recorder existed.
      if (remoteSharingRef.current) rec?.setRemoteSharing(true);
      recorderRef.current = rec;
    }

    let agentLink: AgentPeerLink | null = null;
    const watcher = isCaller ? null : watchForOffer(handlers);

    if (isCaller) {
      void startAgentOffer(handlers).then((link) => {
        agentLink = link;
      });
    }

    // Visitor screen-share hint → caption the recording's main frame as
    // "<visitor> · screen". The pixels already arrive on the remote track.
    const offScreenShare = getBus().on('screen:share', (e) => {
      if (e.sessionId !== conversation.sessionId) return;
      remoteSharingRef.current = e.sharing;
      setRemoteSharing(e.sharing);
      recorderRef.current?.setRemoteSharing(e.sharing);
    });

    return () => {
      offScreenShare();
      remoteSharingRef.current = false;
      setRemoteSharing(false);
      // Stop the recorder first so the trailing chunk is flushed to IDB
      // before the underlying streams are torn down.
      if (recorderRef.current) {
        const rec = recorderRef.current;
        recorderRef.current = null;
        void rec.stop();
      }
      remoteStreamRef.current = null;
      setRecordingSeq(0);
      watcher?.stop();
      agentLink?.stop();
      handle.stop();
      placeholderRef.current = null;
      pcRef.current = null;
      localStreamRef.current = null;
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((t) => t.stop());
        screenStreamRef.current = null;
      }
      setIsSharingScreen(false);
    };
    // IMPORTANT: depend on `me?.agentId` (a stable string), NOT the whole
    // `me` profile object. The agent store resets `current` to a fresh
    // object on every token refresh / profile update / avatar upload,
    // which would otherwise tear down + rebuild the entire WebRTC + recording
    // pipeline mid-call. visitorName is also dropped from the deps because
    // it can change (visitor sets their name pre-chat) without needing the
    // call to restart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conversation.inVideo,
    conversation.sessionId,
    conversation.visitorId,
    isCaller,
    me?.agentId,
  ]);

  // When inVideo flips back to false, also clear the caller flag so the next
  // call on this conversation starts fresh.
  useEffect(() => {
    if (!conversation.inVideo) clearCaller(conversation.sessionId);
  }, [conversation.inVideo, conversation.sessionId, clearCaller]);

  function toggleMic() {
    const stream = localStreamRef.current;
    if (!stream) return;
    const tracks = stream.getAudioTracks();
    if (tracks.length === 0) return;
    const next = !tracks[0]!.enabled;
    tracks.forEach((t) => (t.enabled = next));
    setMicOn(next);
  }

  function toggleCam() {
    const stream = localStreamRef.current;
    if (!stream) return;
    const tracks = stream.getVideoTracks();
    if (tracks.length === 0) return;
    const next = !tracks[0]!.enabled;
    tracks.forEach((t) => (t.enabled = next));
    setCamOn(next);
  }

  // Screen share: replace the camera track on the live PC with a
  // getDisplayMedia track. Stop reverts to the camera. No SDP renegotiation
  // is needed on modern (unified-plan) browsers.
  async function toggleScreenShare() {
    const pc = pcRef.current;
    if (!pc) return;
    if (isSharingScreen) {
      const cam = localStreamRef.current?.getVideoTracks()[0];
      if (cam) await replaceVideoTrack(pc, cam).catch(() => { });
      // Revert the recording composite to the default remote-main layout
      // BEFORE stopping the screen tracks, so the canvas never reads a dead
      // track.
      recorderRef.current?.setScreen(null);
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((t) => t.stop());
        screenStreamRef.current = null;
      }
      setIsSharingScreen(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: false,
      });
      const track = stream.getVideoTracks()[0];
      if (!track) return;
      // Browser's "Stop sharing" chip ends the track — flip our state.
      track.addEventListener('ended', () => {
        if (!screenStreamRef.current) return;
        void toggleScreenShare();
      });
      await replaceVideoTrack(pc, track).catch(() => { });
      // Feed the screen into the recording composite as the main frame —
      // replaceVideoTrack above only changes what the visitor receives, not
      // the local stream the recorder draws, so without this the agent's
      // shared screen never lands in the recording.
      recorderRef.current?.setScreen(stream);
      screenStreamRef.current = stream;
      setIsSharingScreen(true);
    } catch {
      /* user dismissed picker — silent */
    }
  }

  // ─── Camera source selection ───
  // Once the call is live (permission granted, so device labels populate),
  // enumerate cameras and track the active one. Refresh on hot-plug.
  useEffect(() => {
    if (!conversation.inVideo) {
      setCameras([]);
      setShowCamMenu(false);
      return;
    }
    let cancelled = false;
    const refresh = () => {
      void listCameras().then((cams) => {
        if (cancelled) return;
        setCameras(cams);
        // Seed the active id from the live track if we don't have one yet.
        const active = localStreamRef.current?.getVideoTracks()[0];
        const activeId = active?.getSettings().deviceId ?? null;
        if (activeId) setCurrentCameraId(activeId);
      });
    };
    refresh();
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh);
    return () => {
      cancelled = true;
      navigator.mediaDevices?.removeEventListener?.('devicechange', refresh);
    };
    // recordingSeq advances once media is flowing — re-run then so the active
    // track's deviceId/labels are available.
  }, [conversation.inVideo, recordingSeq]);

  // Switch to a different camera mid-call: grab a video-only stream from the
  // chosen device, swap it onto the live peer connection (unless we're screen
  // sharing — then it just becomes the camera we revert to), and into our local
  // stream so the self-view + recording follow. The choice is remembered for
  // future calls.
  async function switchCamera(deviceId: string) {
    setShowCamMenu(false);
    if (deviceId === currentCameraId) return;
    const oldStream = localStreamRef.current;
    if (!oldStream) return;
    let next: MediaStream;
    try {
      next = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
    } catch {
      useNotificationsStore.getState().push({
        kind: 'system',
        title: 'Couldn’t switch camera',
        body: 'That camera is unavailable or in use by another app.',
        ttl: 5000,
      });
      return;
    }
    const newTrack = next.getVideoTracks()[0];
    if (!newTrack) return;
    // Preserve the current on/off state so switching doesn't un-mute the camera.
    newTrack.enabled = camOn;
    // Only push to the wire when the camera is the active sender (not while
    // screen sharing — the revert path picks up the new track from localStream).
    if (pcRef.current && !isSharingScreen) {
      await replaceVideoTrack(pcRef.current, newTrack).catch(() => {});
    }
    // Swap the track into the existing local MediaStream so the self-view and
    // the recorder (both bound to this stream) follow without re-wiring.
    const oldTrack = oldStream.getVideoTracks()[0];
    if (oldTrack) {
      oldStream.removeTrack(oldTrack);
      oldTrack.stop();
    }
    oldStream.addTrack(newTrack);
    if (localVideoRef.current) localVideoRef.current.srcObject = oldStream;
    setCurrentCameraId(deviceId);
    setPreferredCameraId(deviceId);
  }

  // Broadcast video-state changes so OTHER agents / admin watching the
  // monitor can render "video · <Agent>" on this visitor's row in real time.
  // We hop on the existing `agent:status:in-call` event with the optional
  // `videoSessionId` field.
  useEffect(() => {
    if (!me) return;
    const bus = getBus();
    bus.emit({
      type: 'agent:status:in-call',
      agentId: me.agentId,
      pausedSessionIds: [],
      isInCall: conversation.inVideo,
      ...(conversation.inVideo ? { videoSessionId: conversation.sessionId } : {}),
      at: Date.now(),
    });
  }, [conversation.inVideo, conversation.sessionId, me]);

  // ─── Auto-scroll on new messages ─────────────────────────────────
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [conversation.messages.length]);

  // ─── Subtle "fresh message" flash on the MESSAGES rule during calls ──
  // While a video call is live, the agent's attention is on the tiles;
  // new chat messages can slip by unnoticed. Flash the MESSAGES rule for
  // ~1.5s to catch the eye without being intrusive (no toast, no sound).
  const [messageFlash, setMessageFlash] = useState(false);
  const lastSeenCountRef = useRef<number>(conversation.messages.length);
  useEffect(() => {
    const count = conversation.messages.length;
    if (count <= lastSeenCountRef.current) {
      lastSeenCountRef.current = count;
      return;
    }
    const newOnes = conversation.messages.slice(lastSeenCountRef.current);
    lastSeenCountRef.current = count;
    if (!conversation.inVideo) return;
    // Only flash on inbound messages — the agent's own send shouldn't fire it.
    const fromOther = newOnes.some(
      (m) => m.author === 'visitor' || m.author === 'ai',
    );
    if (!fromOther) return;
    setMessageFlash(true);
    const t = window.setTimeout(() => setMessageFlash(false), 1500);
    return () => window.clearTimeout(t);
  }, [conversation.messages, conversation.inVideo]);

  // ─── Send ────────────────────────────────────────────────────────
  // ─── Human-agent typing → visitor ("agent is typing…") ───
  // The widget already renders agent:typing; we just need to emit it while the
  // agent composes. Debounced: emit true on the first keystroke, then false
  // after a short idle (or immediately on send / conversation change).
  const typingRef = useRef<{ active: boolean; timer: number | null }>({
    active: false,
    timer: null,
  });
  function emitAgentTyping(isTyping: boolean) {
    if (!me) return;
    try {
      getBus().emit({
        type: 'agent:typing',
        sessionId: conversation.sessionId,
        visitorId: conversation.visitorId,
        isTyping,
        author: 'agent',
        at: Date.now(),
      });
    } catch {
      /* fire-and-forget */
    }
  }
  function noteAgentTyping() {
    if (visitorOffline) return; // no one to notify
    const t = typingRef.current;
    if (!t.active) {
      t.active = true;
      emitAgentTyping(true);
    }
    if (t.timer) window.clearTimeout(t.timer);
    t.timer = window.setTimeout(() => {
      t.active = false;
      t.timer = null;
      emitAgentTyping(false);
    }, 4000);
  }
  function stopAgentTyping() {
    const t = typingRef.current;
    if (t.timer) {
      window.clearTimeout(t.timer);
      t.timer = null;
    }
    if (t.active) {
      t.active = false;
      emitAgentTyping(false);
    }
  }
  // Stop typing if the agent navigates away / switches conversation mid-compose.
  useEffect(() => {
    return () => stopAgentTyping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.sessionId]);

  // ─── Star / archive / delete (Tier 3) ───
  const canDelete =
    me?.role === 'super-admin' || me?.role === 'account-admin' || me?.role === 'site-admin';
  const [deleting, setDeleting] = useState(false);
  function toggleStar() {
    const next = !conversation.starred;
    setStarred(conversation.sessionId, next); // optimistic
    void setSessionStarred(conversation.sessionId, next).catch(() => {
      setStarred(conversation.sessionId, !next); // revert on failure
    });
  }
  // ─── Email + manual webhook trigger ───
  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const ticketsEnabled = useFeatureFlag('tickets');
  function notify(title: string, kind: 'system' | 'escalate' = 'system') {
    useNotificationsStore.getState().push({ kind, title });
  }
  async function sendEmail() {
    setActionBusy(true);
    setActionsOpen(false);
    try {
      await sendConversationEmail(conversation.sessionId);
      notify('Chat-end email sent');
    } catch (e) {
      const reason =
        isApiError(e) && e.body && typeof e.body === 'object' && 'reason' in e.body
          ? String((e.body as { reason: unknown }).reason)
          : 'send failed';
      notify(`Email not sent: ${reason}`);
    } finally {
      setActionBusy(false);
    }
  }
  // One ticket per conversation in practice: reopen the existing active one
  // rather than raising a duplicate. Subject, site and customer come from the
  // session on the server; assignment is automatic.
  async function openTicket() {
    setActionBusy(true);
    try {
      const existing = await listTickets({ sessionId: conversation.sessionId, status: 'active' });
      if (existing.items[0]) {
        navigate(`/tickets/${existing.items[0].id}`);
        return;
      }
      const { ticket } = await createTicket({ sessionId: conversation.sessionId });
      notify(`Ticket #${ticket.number} created`);
      navigate(`/tickets/${ticket.id}`);
    } catch {
      notify('Could not create a ticket');
    } finally {
      setActionBusy(false);
    }
  }
  async function triggerHook(event: 'session.ended' | 'visitor.identified') {
    setActionBusy(true);
    setActionsOpen(false);
    try {
      const r = await triggerConversationWebhook(conversation.sessionId, event);
      notify(`${event}: fired ${r.fired} webhook${r.fired === 1 ? '' : 's'}`);
    } catch {
      notify(`Could not trigger ${event}`);
    } finally {
      setActionBusy(false);
    }
  }

  function archiveConversation() {
    // End the live session (server marks it ended) but DON'T delete — the log
    // stays reachable from the monitor/history. Drop it from the active rail.
    if (conversation.inVideo) hangupAndNotify(conversation.sessionId, 'ended');
    // Force the session.ended pipeline server-side (webhooks + chat-end email)
    // so the bound follow-up events run reliably, not only via the realtime bus.
    void endConversation(conversation.sessionId, 'resolved').catch(() => {
      /* the bus session:end below is the fallback */
    });
    try {
      getBus().emit({
        type: 'session:end',
        sessionId: conversation.sessionId,
        endedBy: 'agent',
        reason: 'resolved',
        at: Date.now(),
      });
    } catch {
      /* fire-and-forget */
    }
    close(conversation.sessionId);
    navigate('/dashboard');
  }
  async function handleDelete() {
    if (!canDelete || deleting) return;
    const ok = await confirmDialog({
      title: 'Delete this conversation?',
      message: 'It will be hidden from all agents. The log is soft-deleted (kept in the database), not erased.',
    });
    if (!ok) return;
    setDeleting(true);
    try {
      // Force the session.ended pipeline BEFORE soft-deleting (once deleted, the
      // session is unreachable to the end/webhook routes), so bound events run.
      await endConversation(conversation.sessionId, 'resolved').catch(() => {
        /* best-effort — the delete below is what matters */
      });
      await deleteSession(conversation.sessionId);
      if (conversation.inVideo) hangupAndNotify(conversation.sessionId, 'ended');
      close(conversation.sessionId);
      notify('Conversation deleted');
      navigate('/dashboard');
    } catch {
      // Stay on the page so the agent can retry; surface why it failed.
      setDeleting(false);
      useNotificationsStore.getState().push({
        kind: 'system',
        title: 'Couldn’t delete',
        body: 'You may not have permission, or the server rejected it.',
        ttl: 6000,
      });
    }
  }

  function sendMessage() {
    const text = draft.trim();
    if (!me) return;
    // Allow sending message with just attachments (no text) so the agent
    // can ship a file without a comment. Block only when both are empty.
    if (!text && attachments.length === 0) return;
    // Deferred assignment: a cold agent-initiated chat isn't claimed until the
    // agent actually replies. The first message is where we create the session
    // row (server needs it before the message — emitted first; per-socket
    // ordering on the gateway guarantees it commits ahead of the message) and
    // claim ownership so the monitor's ENGAGED pill appears only now.
    const isFirstAgentMessage =
      conversation.startedBy === 'agent' &&
      conversation.medium === 'chat' &&
      conversation.messages.length === 0;
    if (isFirstAgentMessage) {
      getBus().emit({
        type: 'session:start',
        sessionId: conversation.sessionId,
        visitorId: conversation.visitorId,
        agentId: me.agentId,
        medium: 'chat',
        startedBy: 'agent',
        at: conversation.startedAt,
      });
      useConversationsStore.getState().setOwner(conversation.sessionId, 'agent', me.agentId);
    }
    const msg: ConversationMessage = {
      id: newMessageId(),
      sessionId: conversation.sessionId,
      author: 'agent',
      authorId: me.agentId,
      authorName: me.name,
      ...(me.avatarUrl ? { authorAvatarUrl: me.avatarUrl } : {}),
      text,
      sentAt: Date.now(),
      deliveryState: 'sending',
      ...(attachments.length > 0 ? { attachments } : {}),
    };
    append(conversation.sessionId, msg);
    stopAgentTyping();
    setDraft('');
    setAttachments([]);
    // Emit to the bus → visitor widget appends as an agent message.
    // Phase 5: server's authority check requires authorId === JWT.agentId
    // for agent messages — using `me.agentId` (UUID) is correct here.
    getBus().emit({
      type: 'agent:chat:message',
      sessionId: conversation.sessionId,
      message: {
        messageId: msg.id,
        sessionId: conversation.sessionId,
        author: 'agent',
        authorId: me.agentId,
        authorName: me.name,
        // Surface the agent's avatar URL so the visitor widget renders the
        // photo instead of initials when the agent replies in chat.
        ...(me.avatarUrl ? { authorAvatarUrl: me.avatarUrl } : {}),
        text,
        sentAt: msg.sentAt,
        ...(msg.attachments && msg.attachments.length > 0 ? { attachments: msg.attachments } : {}),
      },
    });
    // The canned reply this message came from may also triage the chat.
    const macro = pendingMacro.current;
    pendingMacro.current = null;
    if (macro) {
      void updateInbox({
        ...(macro.status ? { status: macro.status } : {}),
        ...(macro.priority ? { priority: macro.priority } : {}),
        ...(macro.addTags?.length ? { tags: mergeTags(conversation.tags ?? [], macro.addTags) } : {}),
      });
    }
  }

  useEffect(() => {
    if (!me) return;
    const markVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const current = useConversationsStore.getState().byId.get(conversation.sessionId);
      if (!current) return;
      for (const message of current.messages) {
        if (message.author !== 'visitor' || message.deliveryState === 'read') continue;
        const at = Date.now();
        useConversationsStore.getState().markMessageReceipt(
          conversation.sessionId,
          message.id,
          'read',
          at,
        );
        getBus().emit({
          type: 'message:receipt',
          sessionId: conversation.sessionId,
          messageId: message.id,
          status: 'read',
          party: 'agent',
          actorId: me.agentId,
          at,
        });
      }
    };
    markVisible();
    document.addEventListener('visibilitychange', markVisible);
    return () => document.removeEventListener('visibilitychange', markVisible);
  }, [conversation.sessionId, conversation.messages, me]);

  function handleHandoff() {
    if (!me) return;
    // Flip ownership → AI orchestrator will pick up the next visitor message.
    useConversationsStore.getState().setOwner(conversation.sessionId, 'ai');
    // Tell the visitor's widget so it can update its header.
    getBus().emit({
      type: 'ai:takeover',
      sessionId: conversation.sessionId,
      visitorId: conversation.visitorId,
      reason: 'agent-handoff',
      at: Date.now(),
    });
    // Persisted system chip — visible on both sides, survives reload.
    emitSystemMessage(
      conversation.sessionId,
      `${me.name} handed the conversation off to the AI assistant.`,
    );
  }

  function handleTakeBack() {
    if (!me) return;
    useConversationsStore.getState().setOwner(conversation.sessionId, 'agent', me.agentId);
    // Tell the visitor's widget the human took over (re-using ai:escalate
    // with trigger=agent-now-available → visitor's "switch to human" path).
    getBus().emit({
      type: 'ai:escalate',
      sessionId: conversation.sessionId,
      visitorId: conversation.visitorId,
      trigger: 'agent-now-available',
      at: Date.now(),
    });
    emitSystemMessage(
      conversation.sessionId,
      `${me.name} took the conversation back from the AI.`,
    );
  }

  async function runSummary(opts: {
    includeChat: boolean;
    includeNotes: boolean;
    includeTranscripts: boolean;
    extra: string;
  }) {
    setSummarising(true);
    try {
      // Build a labelled prompt from whatever sources the agent picked. The
      // model receives a single user turn — without labels it can't tell
      // visitor lines from agent lines (both collapse to 'assistant' under
      // OpenAI/Anthropic role conventions).
      const sections: string[] = [];
      if (opts.includeChat) {
        const lines = conversation.messages
          .filter(
            (m) =>
              m.author === 'visitor' ||
              m.author === 'agent' ||
              m.author === 'ai' ||
              m.author === 'system',
          )
          .map((m) => {
            const label =
              m.author === 'visitor'
                ? '[Visitor]'
                : m.author === 'ai'
                  ? '[AI]'
                  : m.author === 'system'
                    ? '[System]'
                    : `[Agent: ${m.authorName ?? 'unknown'}]`;
            return `${label}: ${m.text}`;
          });
        if (lines.length > 0) {
          sections.push(`## Chat transcript\n${lines.join('\n')}`);
        }
      }
      if (opts.includeNotes && conversation.notes.trim()) {
        sections.push(`## Agent notes\n${conversation.notes.trim()}`);
      }
      if (opts.includeTranscripts && conversation.callTranscripts.size > 0) {
        const blocks: string[] = [];
        for (const t of conversation.callTranscripts.values()) {
          if (t.body.trim()) {
            blocks.push(
              `### Call ${t.callId.slice(0, 8)} · ${new Date(t.createdAt).toISOString()}\n${t.body.trim()}`,
            );
          }
        }
        if (blocks.length > 0) {
          sections.push(`## Audio transcripts\n${blocks.join('\n\n')}`);
        }
      }
      if (opts.extra.trim()) {
        sections.push(`## Additional context from the agent\n${opts.extra.trim()}`);
      }
      if (sections.length === 0) {
        useNotificationsStore.getState().push({
          kind: 'system',
          title: 'Nothing to summarise',
          body: 'Pick at least one source or add some extra context, then try again.',
          ttl: 5000,
        });
        return;
      }
      const turns: ChatTurn[] = [
        {
          role: 'user',
          content: `${sections.join('\n\n')}\n\n---\nSummarize per the instructions above.`,
        },
      ];
      const summary = await generateSummaryForConversation(conversation.sessionId, turns);
      if (!summary) {
        useNotificationsStore.getState().push({
          kind: 'system',
          title: 'Summary came back empty',
          body: 'The LLM returned no text. Check provider config and try again.',
          ttl: 7000,
        });
        return;
      }
      const generatedAt = Date.now();
      // Persist through the existing bus → gateway → transcripts table path
      // (gateway listens for `ai:summary` and writes a row, upserting on
      // session_id). Local store updates immediately for instant UI feedback.
      getBus().emit({
        type: 'ai:summary',
        sessionId: conversation.sessionId,
        visitorId: conversation.visitorId,
        summary,
        at: generatedAt,
      });
      setSummary(conversation.sessionId, summary, 'agent-llm', generatedAt);
      // Light transcript marker for the record — the full summary is shown
      // (and copyable) in the Summary popup, which we leave open on success.
      useConversationsStore.getState().append(conversation.sessionId, {
        id: `sys_summary_${generatedAt}` as MessageId,
        sessionId: conversation.sessionId,
        author: 'system',
        text: 'AI summary generated.',
        sentAt: generatedAt,
      });
      // Leave `showSummaryPicker` open so the result renders inside the popup
      // with a Copy button (see SummaryPicker).
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Summary failed';
      useNotificationsStore.getState().push({
        kind: 'system',
        title: 'Couldn’t generate summary',
        body: msg.slice(0, 120),
        ttl: 7000,
      });
    } finally {
      setSummarising(false);
    }
  }

  // Show the handling agent's photo + name when a specific (human) agent owns
  // the chat and it isn't the current agent — that's the case the raw agent ID
  // leaked in. Resolves the owner even when they're OFFLINE (via the persisted
  // ownerAgentName/avatar from the server, not live presence).
  const ownedByMe = Boolean(
    conversation.ownerAgentId && me && conversation.ownerAgentId === me.agentId,
  );
  const showOwnerAgent =
    conversation.ownedBy === 'agent' &&
    !ownedByMe &&
    Boolean(conversation.ownerAgentName || conversation.ownerAgentAvatarUrl);

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <section className={styles.root} data-in-video={conversation.inVideo ? 'true' : undefined}>
      <div className={styles.workspace}>
        <header className={styles.head}>
          <div className={styles.headMain}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={() => navigate('/dashboard')}
              aria-label="Back to the list"
              title="Back"
            >
              ←
            </button>
            <div>
            <h1 className={styles.title}>
              {conversation.visitorName}
              {conversation.visitorName !== pseudonymForUuid(conversation.visitorId).display ? (
                <span className={styles.pseudoHandle} title="Stable visitor handle">
                  {pseudonymForUuid(conversation.visitorId).display}
                </span>
              ) : null}
              {offChannel ? (
                <span className={styles.aiBadge} title={`This customer wrote in via ${channelLabel}; replies are delivered there`}>
                  via {channelLabel}
                </span>
              ) : visitorIsLive ? (
                <span className={styles.liveBadge} title="Visitor is on-site right now">
                  LIVE
                </span>
              ) : (
                <span className={styles.offlineBadge} title="Visitor is not on-site">
                  offline
                </span>
              )}
              {conversation.ownedBy === 'ai' ? (
                <span className={styles.aiBadge}>AI</span>
              ) : null}
              {survey?.status === 'answered' ? (
                <span
                  className={styles.aiBadge}
                  title={survey.comment ? `Customer rating — "${survey.comment}"` : 'Customer rating'}
                >
                  ★ {survey.rating}/5
                </span>
              ) : survey?.status === 'sent' ? (
                <span className={styles.offlineBadge} title="A satisfaction survey was sent; no answer yet">
                  survey sent
                </span>
              ) : null}
              {siteName ? (
                <span
                  className={styles.siteBadge}
                  style={siteBadgeStyle(site)}
                  title={`Site: ${siteName}`}
                >
                  {siteName}
                </span>
              ) : null}
            </h1>
            {showOwnerAgent ? (
              <div
                className={styles.handledBy}
                title={`Handled by ${conversation.ownerAgentName ?? 'agent'}`}
              >
                {conversation.ownerAgentAvatarUrl ? (
                  <img
                    className={styles.handledByAvatarImg}
                    src={resolveAvatarUrl(conversation.ownerAgentAvatarUrl)}
                    alt=""
                    aria-hidden
                  />
                ) : (
                  <span className={styles.handledByAvatar} aria-hidden>
                    {getInitials(conversation.ownerAgentName ?? 'Agent')}
                  </span>
                )}
                <span className={styles.handledByName}>
                  {conversation.ownerAgentName ?? 'Agent'}
                </span>
              </div>
            ) : null}
            </div>
          </div>
          {/* Header action cluster — conversation management (Hand off / Take
            over · Summary · Archive · Delete) sits alongside the comms / call
            controls, all contained within the chat/video container's header. */}
          <div className={styles.headActions}>

            {/* Conversation management — present in both chat + call states. */}
            {conversation.ownedBy === 'ai' ? (
              <button
                type="button"
                className={styles.controlPrimary}
                onClick={() => handleTakeBack()}
                disabled={visitorOffline}
                title={
                  visitorOffline
                    ? 'Visitor is offline'
                    : 'Take over the conversation from the AI assistant'
                }
              >
                <UserCheck size={14} aria-hidden />
                <span>Take over</span>
              </button>
            ) : (
              <button
                type="button"
                className={styles.controlGhost}
                onClick={() => handleHandoff()}
                disabled={visitorOffline}
                title={
                  visitorOffline
                    ? 'Visitor is offline'
                    : 'Hand the conversation back to the AI assistant'
                }
              >
                <Bot size={14} aria-hidden />
                <span>Hand off</span>
              </button>
            )}
            <button
              type="button"
              className={styles.starBtn}
              onClick={() => setShowSummaryPicker(true)}
              disabled={summarising || conversation.messages.length === 0}
              title="Generate an AI summary of this conversation"
              aria-label="Summary"
            >
              <Sparkles size={16} aria-hidden />
            </button>
            <button
              type="button"
              className={styles.starBtn}
              onClick={archiveConversation}
              title="End & archive — drops it from the active rail; the log stays available from the monitor"
              aria-label="Archive conversation"
            >
              <Archive size={16} aria-hidden />
            </button>
            {canDelete ? (
              <button
                type="button"
                className={styles.starBtn}
                onClick={() => void handleDelete()}
                disabled={deleting}
                aria-busy={deleting}
                title="Delete this conversation (soft delete — hidden from all agents, kept in the DB)"
                aria-label="Delete conversation"
              >
                <Trash2 size={16} aria-hidden />
              </button>
            ) : null}

            <span className={styles.headDivider} aria-hidden />

            {conversation.inVideo ? (
              // During a call the header carries the call controls (mic · cam ·
              // share · pop-out · End), individual buttons per agent-normal-call.
              <div className={styles.headControls}>
                {/* Recording is automatic; this is a live indicator (accent =
                  recording), not a toggle. */}
                <span
                  className={styles.videoControl}
                  data-recording={recordingSeq > 0 ? 'true' : 'false'}
                  title={recordingSeq > 0 ? 'Recording' : 'Recording will start on connect'}
                  aria-label={recordingSeq > 0 ? 'Recording in progress' : 'Not yet recording'}
                >
                  <span className={styles.recDot} aria-hidden />
                </span>
                <VideoControl
                  kind="mic"
                  state={micOn ? 'on' : 'off'}
                  onClick={toggleMic}
                  className={styles.videoControl}
                  disabled={!localStreamRef.current}
                />
                <VideoControl
                  kind="cam"
                  state={camOn ? 'on' : 'off'}
                  onClick={toggleCam}
                  className={styles.videoControl}
                  disabled={!localStreamRef.current}
                />
                {/* Camera source picker — only when there's a choice to make. */}
                {cameras.length > 1 ? (
                  <span className={styles.camPicker}>
                    <button
                      type="button"
                      className={styles.camPickerBtn}
                      onClick={() => setShowCamMenu((o) => !o)}
                      aria-haspopup="menu"
                      aria-expanded={showCamMenu}
                      title="Choose camera"
                      aria-label="Choose camera"
                      disabled={!localStreamRef.current}
                    >
                      <ChevronDown size={14} aria-hidden />
                    </button>
                    {showCamMenu ? (
                      <div
                        role="menu"
                        className={styles.camMenu}
                        onMouseLeave={() => setShowCamMenu(false)}
                      >
                        {cameras.map((cam, i) => (
                          <button
                            key={cam.deviceId || i}
                            type="button"
                            role="menuitemradio"
                            aria-checked={cam.deviceId === currentCameraId}
                            className={styles.camMenuItem}
                            onClick={() => void switchCamera(cam.deviceId)}
                          >
                            <span className={styles.camMenuCheck} aria-hidden>
                              {cam.deviceId === currentCameraId ? <Check size={13} /> : null}
                            </span>
                            <span className={styles.camMenuLabel}>
                              {cam.label || `Camera ${i + 1}`}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </span>
                ) : null}
                <VideoControl
                  kind="share"
                  state={isSharingScreen ? 'on' : 'off'}
                  onClick={() => void toggleScreenShare()}
                  className={styles.videoControl}
                  disabled={!pcRef.current}
                />

                {typeof document !== 'undefined' && document.pictureInPictureEnabled ? (
                  <button
                    type="button"
                    className={styles.videoControl}
                    onClick={async () => {
                      const node = videoRef.current;
                      if (!node) return;
                      try {
                        if (document.pictureInPictureElement === node) {
                          await document.exitPictureInPicture();
                          return;
                        }
                        await node.requestPictureInPicture();
                      } catch {
                        /* user cancelled or browser refused — silent */
                      }
                    }}
                    title="Pop the visitor's video into a floating window"
                    aria-label="Pop out the visitor's video"
                  >
                    <PictureInPicture2 size={16} aria-hidden />
                  </button>
                ) : null}
                <button
                  type="button"
                  className={styles.headEndBtn}
                  onClick={() => hangupAndNotify(conversation.sessionId, 'ended')}
                  title="End the video call (keeps the chat open)"
                  aria-label="End call"
                >
                  <PhoneOff size={14} aria-hidden />
                  <span>End</span>
                </button>
              </div>
            ) : (
              <>
                {conversation.medium === 'chat' && !offChannel ? (
                  <CallVisitorButton conversation={conversation} forceDisabled={visitorOffline} />
                ) : null}
                <button
                  type="button"
                  className={styles.starBtn}
                  onClick={() => void sendEmail()}
                  disabled={actionBusy}
                  title="Email the transcript, notes & summary to the configured recipients"
                  aria-label="Send chat-end email"
                >
                  <Mail size={16} aria-hidden />
                </button>
                {ticketsEnabled && (
                  <button
                    type="button"
                    className={styles.starBtn}
                    onClick={() => void openTicket()}
                    disabled={actionBusy}
                    title="Create a follow-up ticket from this conversation (or open its existing one)"
                    aria-label="Create ticket"
                  >
                    <TicketIcon size={16} aria-hidden />
                  </button>
                )}
                {/* Comment out dev test for manual webhook trigger
                <button
                  type="button"
                  className={[styles.starBtn, actionsOpen ? styles.starBtnActive : ''].join(' ')}
                  onClick={() => setActionsOpen((o) => !o)}
                  disabled={actionBusy}
                  aria-haspopup="menu"
                  aria-expanded={actionsOpen}
                  title="Manually trigger a webhook (testing)"
                  aria-label="Webhook actions"
                > 
                <Zap size={16} aria-hidden />
              </button>
                */}
                {actionsOpen && (
                  <div
                    role="menu"
                    className={styles.actionsMenu}
                    onMouseLeave={() => setActionsOpen(false)}
                  >
                    <button type="button" role="menuitem" className={styles.actionsItem} onClick={() => void triggerHook('session.ended')}>
                      Trigger session.ended
                    </button>
                    <button type="button" role="menuitem" className={styles.actionsItem} onClick={() => void triggerHook('visitor.identified')}>
                      Trigger visitor.identified
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className={[styles.starBtn, conversation.starred ? styles.starBtnActive : ''].join(' ')}
                  onClick={toggleStar}
                  aria-pressed={!!conversation.starred}
                  title={conversation.starred ? 'Unstar this conversation' : 'Star this conversation (shared with your team)'}
                  aria-label={conversation.starred ? 'Unstar conversation' : 'Star conversation'}
                >
                  <Star
                    size={16}
                    aria-hidden
                    {...(conversation.starred ? { fill: 'currentColor' } : {})}
                  />
                </button>
              </>
            )}
          </div>
          <div className={styles.inboxBar} aria-label="Agent inbox fields">
            <label className={styles.inboxField}>
              <span>Status</span>
              <select
                value={conversation.status ?? 'open'}
                disabled={inboxBusy}
                onChange={(e) => void updateInbox({ status: e.target.value as NonNullable<ConversationT['status']> })}
              >
                <option value="open">Open</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </label>
            <label className={styles.inboxField}>
              <span>Priority</span>
              <select
                value={conversation.priority ?? 'normal'}
                disabled={inboxBusy}
                onChange={(e) => void updateInbox({ priority: e.target.value as NonNullable<ConversationT['priority']> })}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
            <label className={styles.inboxField}>
              <span>Department</span>
              <select
                value={conversation.departmentId ?? ''}
                disabled={inboxBusy}
                onChange={(e) => void updateInbox({ departmentId: e.target.value || null })}
              >
                <option value="">None</option>
                {siteDepartments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            <label className={styles.inboxField}>
              <span>Assigned to</span>
              <select
                value={conversation.ownerAgentId ?? ''}
                disabled={inboxBusy}
                onChange={(e) => void updateInbox({ assigneeId: e.target.value ? e.target.value as AgentId : null })}
              >
                <option value="">Unassigned</option>
                {assignees.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
              </select>
            </label>
            <IntentField sessionId={conversation.sessionId} className={styles.inboxField} />
            <div className={styles.inboxTags}>
              <span className={styles.inboxLabel}>Tags</span>
              {(conversation.tags ?? []).map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className={styles.tagChip}
                  disabled={inboxBusy}
                  title={`Remove ${tag}`}
                  onClick={() => void updateInbox({ tags: (conversation.tags ?? []).filter((item) => item !== tag) })}
                >
                  #{tag} ×
                </button>
              ))}
              <input
                value={tagDraft}
                maxLength={32}
                placeholder="Add tag"
                aria-label="Add conversation tag"
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                onBlur={addTag}
              />
            </div>
          </div>
        </header>

        {
          conversation.inVideo ? (
            <div className={styles.videoStage} data-pinned={pinnedRole ? 'true' : 'false'}>
              {/* Recording indicator — anchored to the stage (not a tile) so it
              stays visible in every layout, incl. when a tile is full-screened
              or the self-view is a PIP (§1.5). */}
              {recordingSeq > 0 ? (
                <span className={styles.recBadge} title={`Recording — chunk ${recordingSeq}`}>
                  <span className={styles.recDot} aria-hidden />
                  REC
                </span>
              ) : null}
              {/* Screenshare status capsule — who's presenting, centred at the top
              of the stage (§ screenshare mock). */}
              {isSharingScreen || remoteSharing ? (
                <span className={styles.shareCapsule}>
                  <MonitorUp size={14} aria-hidden />
                  {isSharingScreen
                    ? `You're presenting to ${conversation.visitorName}`
                    : `${conversation.visitorName} is presenting`}
                </span>
              ) : null}
              <div
                className={styles.videoTile}
                data-role="agent"
                data-slot={slotFor('agent')}
                style={
                  slotFor('agent') === 'pip'
                    ? ({ ['--pip-index' as string]: pipIndexFor('agent') } as React.CSSProperties)
                    : undefined
                }
              >
                <video
                  ref={localVideoRef}
                  className={[styles.video, styles.videoMirror].join(' ')}
                  autoPlay
                  muted
                  playsInline
                />
                <span className={styles.videoLabel}>YOU</span>
                <button
                  type="button"
                  className={styles.tileFsBtn}
                  onClick={() => toggleFullscreenTile('agent')}
                  title={pinnedRole === 'agent' ? 'Exit full screen' : 'Full screen this tile'}
                  aria-label={pinnedRole === 'agent' ? 'Exit full screen' : 'Full screen this tile'}
                >
                  {pinnedRole === 'agent' ? <Minimize2 size={13} aria-hidden /> : <Maximize2 size={13} aria-hidden />}
                </button>
              </div>
              <div
                className={styles.videoTile}
                data-role="visitor"
                data-slot={slotFor('visitor')}
                style={
                  slotFor('visitor') === 'pip'
                    ? ({ ['--pip-index' as string]: pipIndexFor('visitor') } as React.CSSProperties)
                    : undefined
                }
              >
                <video
                  ref={videoRef}
                  className={styles.video}
                  autoPlay
                  muted
                  playsInline
                />
                <span className={styles.videoLabel}>
                  {conversation.visitorName.toUpperCase()}
                </span>
                <button
                  type="button"
                  className={styles.tileFsBtn}
                  onClick={() => toggleFullscreenTile('visitor')}
                  title={pinnedRole === 'visitor' ? 'Exit full screen' : 'Full screen this tile'}
                  aria-label={pinnedRole === 'visitor' ? 'Exit full screen' : 'Full screen this tile'}
                >
                  {pinnedRole === 'visitor' ? <Minimize2 size={13} aria-hidden /> : <Maximize2 size={13} aria-hidden />}
                </button>
              </div>
              {/* Call controls live in the header row during a call (see header). */}
            </div>
          ) : null
        }

        <div className={styles.sideTabs} role="tablist" aria-label="Conversation panels">
          {(conversation.inVideo ? IN_CALL_TABS : NO_CALL_TABS).filter((key) => key !== 'shop' || shopConnected).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={effectiveTab === key}
              className={[styles.sideTab, effectiveTab === key ? styles.sideTabActive : ''].join(' ')}
              onClick={() => setSideTab(key)}
            >
              {TAB_LABEL[key]}
              {key === 'recordings' && hasRecording ? (
                <span className={styles.sideTabDot} aria-hidden />
              ) : null}
            </button>
          ))}
        </div>
        <div
          className={styles.chatPane}
          style={conversation.inVideo && sideTab !== 'chat' ? { display: 'none' } : undefined}
        >
          <hr
            className={[styles.rule, messageFlash ? styles.ruleFlash : ''].join(' ')}
            data-label="MESSAGES"
            style={{ marginTop: '8px' }}
          />
          <div
            ref={listRef}
            className={styles.list}
            role="log"
            aria-live="polite"
            aria-label="Messages"
            // Scrolls on its own: focusable so the keyboard can scroll it.
            tabIndex={0}
          >
            {conversation.messages.length === 0 ? (
              <ComposeHint conversation={conversation} />
            ) : (
              conversation.messages.map((m) => (
                <Message
                  key={m.id}
                  message={m}
                  sessionId={conversation.sessionId}
                  mineAgentId={me?.agentId ?? null}
                  visitorName={conversation.visitorName}
                  visitorColor={colorForVisitor(conversation.visitorId)}
                />
              ))
            )}
          </div>
          {conversation.aiPreview?.text ? <p className={styles.typingHint} role="status" aria-label="AI response in progress">{conversation.aiPreview.text}</p> : null}
          {visitorTyping ? (
            <div className={styles.typingHint} aria-live="polite">
              {conversation.visitorName} is typing…
            </div>
          ) : null}
          <ConversationAiUsage siteId={conversation.siteId} sessionId={conversation.sessionId} />
          <div className={styles.composer} data-disabled={visitorOffline ? 'true' : undefined}>
            {suggestError ? (
              <div className={styles.composerAttachStrip} role="alert">
                <span className={styles.composerAttachChip}>
                  <span>{suggestError}</span>
                  <button
                    type="button"
                    className={styles.composerAttachRemove}
                    onClick={() => setSuggestError(null)}
                    aria-label="Dismiss"
                    title="Dismiss"
                  >
                    <XIcon size={12} aria-hidden />
                  </button>
                </span>
              </div>
            ) : null}
            {attachments.length > 0 ? (
              <div className={styles.composerAttachStrip}>
                {attachments.map((a, i) => (
                  <span key={`${a.filename}-${i}`} className={styles.composerAttachChip}>
                    <FileText size={12} aria-hidden />
                    <span>{a.filename}</span>
                    <button
                      type="button"
                      className={styles.composerAttachRemove}
                      onClick={() =>
                        setAttachments((cur) => cur.filter((_, j) => j !== i))
                      }
                      aria-label={`Remove ${a.filename}`}
                      title="Remove"
                    >
                      <XIcon size={12} aria-hidden />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}

            <div className={styles.composerRow}>
              {/* Tools sit before the textbox (a horizontal row out of a call;
                  CSS stacks the whole row vertically in the in-call Chat tab). */}
              <div className={styles.composerTools}>
                {/* A real button driving a hidden file input: the old
                    <label> wrapper was not focusable, so the keyboard could
                    not attach files at all. */}
                <button
                  type="button"
                  className={styles.composerTool}
                  title={
                    offChannel
                      ? `Attachments can't be sent via ${channelLabel}`
                      : visitorOffline
                        ? 'Visitor is offline'
                        : 'Attach files (≤ 5 MB each)'
                  }
                  aria-label="Attach files"
                  disabled={visitorOffline || offChannel}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip size={14} aria-hidden />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv,.docx,.xlsx,.pptx"
                  hidden
                  disabled={visitorOffline || offChannel}
                  onChange={(e) => {
                    void pickAttachments(e.target.files);
                    e.target.value = '';
                  }}
                />
                <span className={styles.composerToolWrap}>
                  <button
                    type="button"
                    className={[
                      styles.composerTool,
                      showEmoji ? styles.composerToolActive : '',
                    ].join(' ')}
                    onClick={() => setShowEmoji((v) => !v)}
                    title="Insert emoji"
                    aria-label="Insert emoji"
                    aria-expanded={showEmoji}
                    disabled={visitorOffline}
                  >
                    <Smile size={14} aria-hidden />
                  </button>
                  {/* Anchored to the button so it opens right beside it. */}
                  {showEmoji ? (
                    <EmojiPicker
                      onPick={(e) => {
                        insertAtCursor(e);
                        // Keep the picker open so agents can drop a few in a row.
                      }}
                      onClose={() => setShowEmoji(false)}
                    />
                  ) : null}
                </span>
                <button
                  type="button"
                  className={[
                    styles.composerTool,
                    slashQuery !== null ? styles.composerToolActive : '',
                  ].join(' ')}
                  onClick={() => {
                    // Quick affordance — clicking jumps to a fresh "/" so the
                    // autocomplete pops without the agent learning the keyboard
                    // trigger.
                    setDraft('/');
                    requestAnimationFrame(() => composerInputRef.current?.focus());
                  }}
                  title="Insert canned reply (or type /)"
                  aria-label="Insert canned reply"
                  disabled={visitorOffline}
                >
                  <Slash size={14} aria-hidden />
                </button>
                <button
                  type="button"
                  className={[styles.composerTool, suggesting ? styles.composerToolActive : ''].join(' ')}
                  onClick={async () => {
                    if (suggesting) return;
                    if (draft.trim() && !(await confirmDialog({ title: 'Replace your draft with a suggested reply?', tone: 'default', confirmLabel: 'Replace draft' }))) return;
                    setSuggesting(true);
                    setSuggestError(null);
                    try {
                      const text = await suggestReply(conversation.sessionId);
                      setDraft(text);
                      requestAnimationFrame(() => {
                        const n = composerInputRef.current;
                        if (!n) return;
                        n.focus();
                        n.setSelectionRange(text.length, text.length);
                      });
                    } catch (err) {
                      setSuggestError(assistantErrorMessage(err));
                    } finally {
                      setSuggesting(false);
                    }
                  }}
                  title={suggesting ? 'Drafting a reply…' : 'Suggest a reply with AI (you review it before sending)'}
                  aria-label="Suggest a reply with AI"
                  aria-busy={suggesting}
                  disabled={suggesting}
                >
                  <Sparkles size={14} aria-hidden />
                </button>
              </div>

              <textarea
                ref={composerInputRef}
                className={styles.composerInput}
                rows={1}
                placeholder={
                  visitorOffline
                    ? 'Visitor is offline — they won’t see new messages until they return.'
                    : conversation.messages.length === 0
                      ? 'Open with a friendly first message — your soft-lock holds 30 seconds.'
                      : 'Reply… (type / for canned replies)'
                }
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  noteAgentTyping();
                }}
                onBlur={() => stopAgentTyping()}
                onKeyDown={(e) => {
                  // Slash menu navigation hijacks arrows + Enter when open.
                  if (slashQuery !== null && slashMatches.length > 0) {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setSlashIdx((i) => Math.min(i + 1, slashMatches.length - 1));
                      return;
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setSlashIdx((i) => Math.max(i - 1, 0));
                      return;
                    }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      const pick = slashMatches[slashIdx];
                      if (pick) applySlash(pick);
                      return;
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setSlashQuery(null);
                      return;
                    }
                  }
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                aria-label="Reply composer"
                disabled={visitorOffline}
              />

              {/* Send sits to the right of the textbox (wide) and drops below
                  it when the composer is stacked in the in-call Chat tab. */}
              <button
                type="button"
                className={styles.send}
                onClick={sendMessage}
                disabled={(!draft.trim() && attachments.length === 0) || visitorOffline}
                aria-label="Send message"
                title={visitorOffline ? 'Visitor is offline' : 'Send (Enter)'}
              >
                <Send size={14} aria-hidden />
                <span>Send</span>
              </button>
            </div>

            {slashQuery !== null ? (
              <div className={styles.slashMenu} role="listbox">
                {slashMatches.length === 0 ? (
                  <p className={styles.slashEmpty}>
                    No commands match "/{slashQuery}". Ask a site admin to add one.
                  </p>
                ) : (
                  slashMatches.map((c, i) => (
                    <button
                      key={c.id}
                      type="button"
                      role="option"
                      aria-selected={i === slashIdx}
                      className={[
                        styles.slashItem,
                        i === slashIdx ? styles.slashItemActive : '',
                      ].join(' ')}
                      onClick={() => applySlash(c)}
                      onMouseEnter={() => setSlashIdx(i)}
                    >
                      <span className={styles.slashShortcut}>/{c.shortcut}</span>
                      <span className={styles.slashLabel}>
                        {c.label}
                        {c.ownerAgentId ? <span className={styles.slashMeta}> · personal</span> : null}
                        {hasActions(c.actions) ? <span className={styles.slashMeta}> · {describeActions(c.actions)}</span> : null}
                      </span>
                      <span className={styles.slashBody}>{c.body}</span>
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>
        </div>

        <aside
          className={styles.notesPane}
          aria-label="Conversation details"
          // Scrolls on its own: focusable so keyboard users can scroll it.
          tabIndex={0}
          style={conversation.inVideo && sideTab === 'chat' ? { display: 'none' } : undefined}
        >
          {/* The info panels are tabs both in and out of a call — only the
              active one renders. Page timeline rides along with Context. */}
          {effectiveTab === 'visitor' ? <VisitorProfile visitorId={conversation.visitorId} /> : null}
          {effectiveTab === 'shop' && conversation.siteId ? (
            <ShopPanel siteId={conversation.siteId} visitorId={conversation.visitorId} />
          ) : null}
          {effectiveTab === 'context' ? (
            <>
              <DeviceContext visitorId={conversation.visitorId} />
              <PageTimeline visitorId={conversation.visitorId} />
            </>
          ) : null}
          {effectiveTab === 'recordings' ? (
            <RecordingsPanel sessionId={conversation.sessionId} />
          ) : null}
          {effectiveTab === 'notes' ? <NotesPane sessionId={conversation.sessionId} /> : null}
        </aside>
      </div >
      {
        showSummaryPicker ? (
          <SummaryPicker
            conversation={conversation}
            busy={summarising}
            onClose={() => setShowSummaryPicker(false)
            }
            onSubmit={(opts) => void runSummary(opts)}
          />
        ) : null}
    </section >
  );
}

function CallVisitorButton({
  conversation,
  forceDisabled = false,
}: {
  conversation: ConversationT;
  forceDisabled?: boolean;
}) {
  const me = useAgentStore((s) => s.current);
  const pending = useOutgoingCallsStore((s) =>
    s.pending.get(conversation.sessionId),
  );
  const push = useOutgoingCallsStore((s) => s.push);
  const drop = useOutgoingCallsStore((s) => s.drop);

  // Ringback tone while waiting for the visitor to pick up.
  useEffect(() => {
    if (pending) {
      playRingtone('ringback');
      return () => stopRingtone('ringback');
    }
    return undefined;
  }, [pending]);

  function startCall() {
    if (!me) return;
    const bus = getBus();
    const sessionId = conversation.sessionId;
    push({
      sessionId,
      to: conversation.visitorId,
      toName: conversation.visitorName,
      at: Date.now(),
    });
    bus.emit({
      type: 'webrtc:invite',
      sessionId,
      from: me.agentId,
      to: conversation.visitorId,
      fromName: me.name,
      // Surface the agent's avatar to the visitor's ring screen so the
      // incoming-call card shows a face, not a generic placeholder. Empty
      // string when no avatar set; the widget treats it as "fall back to
      // initials".
      ...(me.avatarUrl ? { fromAvatarUrl: me.avatarUrl } : {}),
      at: Date.now(),
    });
    // Agent-side transcript chip so the agent has a clear record that THEY
    // started the call. Local-only (not via emitSystemMessage) — the visitor
    // already gets its own "<agent> is calling…" chip from the ring, so we
    // don't want to double-announce on their side.
    useConversationsStore.getState().append(sessionId, {
      id: newMessageId(),
      sessionId,
      author: 'system',
      text: 'You started a video call.',
      sentAt: Date.now(),
    });
  }

  function cancelCall() {
    // Tell the widget to drop its ring/Connecting state; webrtc:hangup with
    // reason='cancelled' funnels through the same teardown path.
    hangupAndNotify(conversation.sessionId, 'cancelled');
    drop(conversation.sessionId);
  }

  if (pending) {
    return (
      <button
        type="button"
        className={styles.controlCalling}
        onClick={cancelCall}
        title="Cancel video call request"
      >
        <XIcon size={14} aria-hidden />
        <span>Calling {conversation.visitorName}…</span>
      </button>
    );
  }
  // Gate cold video calls: don't let an agent ring a visitor who hasn't said
  // anything yet. The button stays visible (so the agent knows the option
  // exists) but is disabled with an explanatory tooltip.
  const visitorHasSpoken = conversation.messages.some((m) => m.author === 'visitor');
  return (
    <button
      type="button"
      className={styles.startVideoBtn}
      onClick={startCall}
      disabled={!visitorHasSpoken || forceDisabled}
      title={
        forceDisabled
          ? 'Visitor is offline'
          : visitorHasSpoken
            ? 'Start a video call with this visitor'
            : 'Visitor hasn’t said anything yet — wait for their reply first'
      }
    >
      <VideoIcon size={15} aria-hidden />
      <span>Start video</span>
    </button>
  );
}

// Relative timestamp that re-renders periodically so "2m ago" stays current.
// 30s cadence is plenty for minute-granularity text and cheap for the handful
// of system chips in a transcript.
function RelativeTime({ at }: { at: number }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);
  return <time dateTime={new Date(at).toISOString()}>{formatRelativeOrAbsolute(at)}</time>;
}

function Message({
  message,
  sessionId,
  mineAgentId,
  visitorName,
  visitorColor,
}: {
  message: ConversationMessage;
  sessionId: SessionId;
  mineAgentId: AgentId | null;
  visitorName: string;
  visitorColor: string;
}) {
  const [retrying, setRetrying] = useState(false);
  async function retryDelivery() {
    setRetrying(true);
    try {
      await retryMessageDelivery(sessionId, message.id);
      // The server also pushes this; setting it now makes the click feel instant.
      useConversationsStore.getState().setDeliveryStatus(sessionId, message.id, 'queued');
    } catch {
      /* stays "not delivered"; the agent can try again */
    } finally {
      setRetrying(false);
    }
  }
  const isAI = message.author === 'ai';
  const isSystem = message.author === 'system';
  // First-person rule on the agent side: the agent's own messages AND the
  // AI's messages both render on the right. From the agent's POV the AI is
  // a co-worker, not a remote party — visually it's "our side" vs visitor.
  const isMine =
    isAI ||
    (message.author === 'agent' && mineAgentId !== null && message.authorId === mineAgentId);
  // Best-known display name per author. Visitor messages from the server
  // come back without authorName (the messages table doesn't store it); use
  // the conversation's visitorName so we don't render literal "Visitor".
  const displayName =
    message.authorName ??
    (message.author === 'visitor' ? visitorName : defaultName(message.author));

  if (isSystem) {
    return (
      <div className={styles.systemRow} data-ts={String(message.sentAt)}>
        <span className={styles.systemChip}>
          {message.text}
          <span className={styles.systemTime}>
            · <RelativeTime at={message.sentAt} />
          </span>
        </span>
      </div>
    );
  }

  const avatarStyle =
    message.author === 'visitor' ? { background: visitorColor, color: '#FAF3E0' } : undefined;

  return (
    <article
      className={styles.msg}
      data-self={isMine ? 'true' : 'false'}
      data-author={message.author}
    >
      {message.authorAvatarUrl && message.author === 'agent' ? (
        <img
          className={styles.msgAvatarImg}
          src={resolveAvatarUrl(message.authorAvatarUrl)}
          alt=""
          aria-hidden
        />
      ) : (
        <span className={styles.msgAvatar} aria-hidden style={avatarStyle}>
          {isAI ? 'ai' : getInitials(displayName)}
        </span>
      )}
      <div className={styles.msgBody}>
        <div className={styles.msgMeta}>
          <span className={styles.msgAuthor}>{displayName}</span>
          <time
            dateTime={new Date(message.sentAt).toISOString()}
            title={formatDateTime(message.sentAt)}
          >
            {formatTime(message.sentAt)}
          </time>
          {message.deliveryStatus === 'failed' ? (
            <>
              <span
                className={styles.msgUndelivered}
                title={`${message.deliveryError ?? 'The channel could not deliver this reply.'} Details are in Admin → Jobs.`}
              >
                not delivered
              </span>
              <button type="button" className={styles.msgRetry} disabled={retrying} onClick={() => void retryDelivery()}>
                {retrying ? 'retrying…' : 'retry'}
              </button>
            </>
          ) : message.deliveryStatus === 'queued' && message.deliveryError ? (
            <span className={styles.msgDelivery} title={message.deliveryError}>
              retrying…
            </span>
          ) : message.deliveryStatus && message.deliveryStatus !== 'queued' ? (
            <span className={styles.msgDelivery}>{message.deliveryStatus}</span>
          ) : null}
        </div>
        <div className={styles.bubble}>
          {message.text ? (
            <div
              className={styles.msgText}
              data-rich={message.richText?.length ? 'true' : undefined}
            >
              {message.richText?.length ? (
                <RichText blocks={message.richText} />
              ) : (
                linkify(message.text)
              )}
            </div>
          ) : null}
          {message.quickReplies?.length ? (
            <div className={styles.quickReplies} aria-label="Buttons offered to the visitor">
              {message.quickReplies.map((o) => (
                <span key={o} className={styles.quickReply}>
                  {o}
                </span>
              ))}
            </div>
          ) : null}
          {message.attachments?.length ? (
            <ul className={styles.attachments}>
              {message.attachments.map((a) =>
                a.mimeType.startsWith('image/') ? (
                  <li key={a.url}>
                    <a
                      href={a.url}
                      download={a.filename}
                      className={styles.attachmentImage}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`${a.filename} · ${formatBytes(a.byteSize)} — click to open`}
                    >
                      <img className={styles.attachmentThumb} src={a.url} alt={a.filename} />
                      <span className={styles.attachmentMeta}>
                        {a.filename} · {formatBytes(a.byteSize)}
                      </span>
                    </a>
                  </li>
                ) : (
                  <li key={a.url}>
                    <a
                      href={a.url}
                      download={a.filename}
                      className={styles.attachment}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className={styles.attachmentName}>{a.filename}</span>
                      <span className={styles.attachmentMeta}>{formatBytes(a.byteSize)}</span>
                      <span className={styles.attachmentDownload} aria-hidden>
                        ↓
                      </span>
                    </a>
                  </li>
                ),
              )}
            </ul>
          ) : null}
        </div>
        {message.citations?.length ? <MessageSources citations={message.citations} /> : null}
        {isMine && message.deliveryState ? (
          <span className={styles.messageReceipt} aria-live="polite">
            {message.deliveryState === 'read'
              ? 'Read'
              : message.deliveryState === 'delivered'
                ? 'Delivered'
                : 'Sending…'}
          </span>
        ) : null}
      </div>
    </article>
  );
}

const SOURCE_KIND_LABEL: Record<string, string> = {
  text: 'Text',
  md: 'Markdown',
  faq: 'FAQ',
  pdf: 'PDF',
  url: 'Web page',
};

/** Only http(s) source URLs become links; anything else renders as text. */
function safeSourceHref(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/** The knowledge-base sources an AI reply was grounded with. Agent-only:
 *  the widget never receives citations. */
function MessageSources({ citations }: { citations: MessageCitation[] }) {
  return (
    <details className={styles.citations}>
      <summary>
        {citations.length === 1 ? '1 source' : `${citations.length} sources`}
      </summary>
      <ol className={styles.citationList}>
        {citations.map((c, i) => {
          const href = safeSourceHref(c.sourceUrl);
          return (
            <li key={`${i}-${c.entryId ?? c.title}`}>
              {href ? (
                <a href={href} target="_blank" rel="noopener noreferrer" title={href}>
                  {c.title}
                </a>
              ) : (
                <span>{c.title}</span>
              )}
              <span className={styles.citationMeta}>
                {SOURCE_KIND_LABEL[c.kind] ?? c.kind}
                {c.entryId === null ? ' · deleted' : ''}
              </span>
            </li>
          );
        })}
      </ol>
    </details>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ComposeHint({ conversation }: { conversation: ConversationT }) {
  // Empty-state shown before the agent's first message. Opening a row is
  // observation; the conversation is only assigned (ENGAGED) once a message is
  // sent — so the copy nudges toward that explicit claim.
  return (
    <div className={styles.softLockHint}>
      <p>
        You opened <strong>{conversation.visitorName}</strong>.
      </p>
      <p className={styles.softLockTimer}>
        Send a message to claim this conversation — it isn't assigned to you until you reply.
      </p>
    </div>
  );
}

// (AccordionSection removed — the info panels are tabs now, in and out of a
// call.)

// (The read-only AI-summary block was removed from the Notes pane — the
// generated summary now lives in the Summary popup with a Copy button.)

// Notes pane: persisted free-text. Debounced autosave (~800ms after the last
// keystroke) plus an explicit Save button so the agent can flush instantly.
// The save state lives in the store so other components can read it.
function NotesPane({ sessionId }: { sessionId: SessionId }) {
  const notes = useConversationsStore((s) => s.byId.get(sessionId)?.notes ?? '');
  const saveState = useConversationsStore(
    (s) => s.byId.get(sessionId)?.notesSaveState ?? 'idle',
  );
  const updatedAt = useConversationsStore(
    (s) => s.byId.get(sessionId)?.notesUpdatedAt ?? null,
  );
  const setNotes = useConversationsStore((s) => s.setNotes);
  const setNotesSaveState = useConversationsStore((s) => s.setNotesSaveState);

  // Track the last body we persisted. Autosave only fires when the live body
  // diverges from this — protects against redundant PUTs on tab focus, etc.
  const lastSavedRef = useRef<string>(notes);
  useEffect(() => {
    if (saveState === 'idle') lastSavedRef.current = notes;
  }, [saveState, notes]);

  const save = useCallback(async () => {
    const body = useConversationsStore.getState().byId.get(sessionId)?.notes ?? '';
    if (body === lastSavedRef.current && saveState !== 'error') return;
    setNotesSaveState(sessionId, 'saving');
    try {
      const { updatedAt } = await putNotes(sessionId, body);
      lastSavedRef.current = body;
      setNotesSaveState(sessionId, 'saved', updatedAt);
    } catch (err) {
       
      console.warn('[notes] save failed', err);
      setNotesSaveState(sessionId, 'error');
    }
  }, [sessionId, saveState, setNotesSaveState]);

  // Debounced autosave. Reset the timer on every keystroke; flush when the
  // user pauses for 800ms.
  useEffect(() => {
    if (saveState !== 'dirty') return;
    const t = window.setTimeout(() => {
      void save();
    }, 800);
    return () => window.clearTimeout(t);
  }, [saveState, notes, save]);

  // Flush on unmount / navigation when there's an unsaved change. `notes`
  // captures the latest body in the closure thanks to the dep array.
  useEffect(() => {
    return () => {
      const current = useConversationsStore.getState().byId.get(sessionId);
      if (current && current.notesSaveState === 'dirty') {
        void putNotes(sessionId, current.notes).catch(() => { });
      }
    };
  }, [sessionId]);

  return (
    <>
      <textarea
        className={styles.notes}
        value={notes}
        onChange={(e) => setNotes(sessionId, e.target.value)}
        placeholder="Private notes — visible only to agents."
        aria-label="Private notes"
      />
      <div className={styles.notesBar}>
        <span className={notesStatusClass(saveState)} aria-live="polite">
          {notesStatusLabel(saveState, updatedAt)}
        </span>
        <button
          type="button"
          className={styles.notesSaveBtn}
          onClick={() => void save()}
          disabled={saveState === 'saving' || saveState === 'idle' || saveState === 'saved'}
          title="Save notes now"
        >
          Save
        </button>
      </div>
    </>
  );
}

function notesStatusClass(state: NotesSaveState): string {
  switch (state) {
    case 'dirty':
      return [styles.notesStatus, styles.notesStatusDirty].join(' ');
    case 'saving':
      return [styles.notesStatus, styles.notesStatusSaving].join(' ');
    case 'saved':
    case 'idle':
      return [styles.notesStatus, styles.notesStatusSaved].join(' ');
    case 'error':
      return [styles.notesStatus, styles.notesStatusError].join(' ');
  }
}
function notesStatusLabel(state: NotesSaveState, updatedAt: number | null): string {
  switch (state) {
    case 'dirty':
      return 'Unsaved changes';
    case 'saving':
      return 'Saving…';
    case 'saved':
      return updatedAt ? `Saved · ${formatTime(updatedAt)}` : 'Saved';
    case 'idle':
      return updatedAt ? `Saved · ${formatTime(updatedAt)}` : 'Up to date';
    case 'error':
      return 'Couldn’t save — click Save to retry';
  }
}

// Summarize-with-AI source picker. Asks the agent every time which sources
// to fold into the prompt. Result is persisted to `transcripts` (overwrites
// any prior summary) and reflected immediately in SummaryBlock.
function SummaryPicker({
  conversation,
  busy,
  onClose,
  onSubmit,
}: {
  conversation: ConversationT;
  busy: boolean;
  onClose: () => void;
  onSubmit: (opts: {
    includeChat: boolean;
    includeNotes: boolean;
    includeTranscripts: boolean;
    extra: string;
  }) => void;
}) {
  const [includeChat, setIncludeChat] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [includeTranscripts, setIncludeTranscripts] = useState(false);
  const [extra, setExtra] = useState('');
  const [copied, setCopied] = useState(false);
  const summary = conversation.summary;
  function copySummary() {
    if (!summary) return;
    void navigator.clipboard
      ?.writeText(summary)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        /* clipboard blocked (insecure context / permissions) — no-op */
      });
  }
  const hasNotes = conversation.notes.trim().length > 0;
  const hasTranscripts = conversation.callTranscripts.size > 0;
  const canSubmit =
    !busy &&
    (includeChat || (includeNotes && hasNotes) || (includeTranscripts && hasTranscripts) ||
      extra.trim().length > 0);

  return (
    <div
      className={styles.modalBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Summarize options"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className={styles.modal}>
        <h2 className={styles.modalTitle}>What should the summary include?</h2>
        <div className={styles.modalBody}>
          <label className={styles.modalCheck}>
            <input
              type="checkbox"
              checked={includeChat}
              onChange={(e) => setIncludeChat(e.target.checked)}
            />
            <span>
              Chat transcript
              <span className={styles.modalCheckHint}>
                Every visitor/agent/AI message in this session.
              </span>
            </span>
          </label>
          <label
            className={[styles.modalCheck, hasNotes ? '' : styles.modalCheckDisabled].join(' ')}
          >
            <input
              type="checkbox"
              checked={includeNotes && hasNotes}
              disabled={!hasNotes}
              onChange={(e) => setIncludeNotes(e.target.checked)}
            />
            <span>
              Private notes
              <span className={styles.modalCheckHint}>
                {hasNotes ? 'Your notes for this conversation.' : 'No notes yet for this conversation.'}
              </span>
            </span>
          </label>
          <label
            className={[styles.modalCheck, hasTranscripts ? '' : styles.modalCheckDisabled].join(' ')}
          >
            <input
              type="checkbox"
              checked={includeTranscripts && hasTranscripts}
              disabled={!hasTranscripts}
              onChange={(e) => setIncludeTranscripts(e.target.checked)}
            />
            <span>
              Audio transcripts
              <span className={styles.modalCheckHint}>
                {hasTranscripts
                  ? `${conversation.callTranscripts.size} call transcript${conversation.callTranscripts.size === 1 ? '' : 's'}.`
                  : 'No call transcripts on record yet.'}
              </span>
            </span>
          </label>
          <textarea
            className={styles.modalTextarea}
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="Anything else to add (optional)…"
            aria-label="Extra context"
          />
        </div>
        {summary ? (
          <div className={styles.summaryResult}>
            <div className={styles.summaryResultHead}>
              <span className={styles.summaryKicker}>Generated summary</span>
              <button
                type="button"
                className={styles.modalBtn}
                onClick={copySummary}
                disabled={busy}
              >
                {copied ? 'Copied ✓' : 'Copy'}
              </button>
            </div>
            <p className={styles.summaryResultBody}>{summary}</p>
          </div>
        ) : null}
        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.modalBtn}
            onClick={onClose}
            disabled={busy}
          >
            {summary ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            className={[styles.modalBtn, styles.modalBtnPrimary].join(' ')}
            onClick={() =>
              onSubmit({ includeChat, includeNotes: includeNotes && hasNotes, includeTranscripts: includeTranscripts && hasTranscripts, extra })
            }
            disabled={!canSubmit}
          >
            {busy ? 'Summarising…' : summary ? 'Regenerate' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// URL detection used by `linkify` below — same regex as the widget so both
// surfaces match. Trailing punctuation that often follows URLs in prose
// (".)?!") is peeled off the match and returned to the surrounding text.
const URL_RE = /\bhttps?:\/\/\S+|\bwww\.[^\s]+/gi;
const TRAILING_PUNCT_RE = /[.,;:!?)\]}'"]+$/;
function linkify(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    if (start > cursor) nodes.push(text.slice(cursor, start));
    let url = m[0];
    let trail = '';
    const trailMatch = url.match(TRAILING_PUNCT_RE);
    if (trailMatch) {
      trail = trailMatch[0];
      url = url.slice(0, -trail.length);
    }
    const href = /^https?:/i.test(url) ? url : `https://${url}`;
    nodes.push(
      <a key={`u${key++}`} href={href} target="_blank" rel="noopener noreferrer" className={styles.msgLink}>
        {url}
      </a>,
    );
    if (trail) nodes.push(trail);
    cursor = end;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

function defaultName(a: ConversationMessage['author']): string {
  switch (a) {
    case 'visitor':
      return 'Visitor';
    case 'agent':
      return 'Agent';
    case 'ai':
      return 'AI Assistant';
    case 'system':
      return 'System';
  }
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}
